import React, { useEffect, useMemo } from 'react';
import { Component, ComponentType } from '@/types';
import { ProjectHubContainer } from '@/components/pm/ProjectHubContainer';
import { useComponentDataResolver } from '@/services/component-data/useComponentDataResolver';
import { registerRefetch, unregisterRefetch } from '@/stores/data/refresh-store';
import { isRecord } from '@/types/componentDataBinding';

export interface SharedProjectHubComponentProps {
  component: Component;
  isViewerMode?: boolean;
}

const DEFAULT_PROJECT_HUB_REFETCH_TAB_IDS = [
  'table',
  'board',
  'timeline',
  'calendar',
];

function isProjectHubChildComponentType(value: unknown): boolean {
  return (
    value === ComponentType.ADVANCED_TABLE ||
    value === ComponentType.KANBAN_BOARD ||
    value === ComponentType.GANTT_CHART ||
    value === ComponentType.CALENDAR
  );
}

function getConfiguredProjectHubTabIds(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const tabIds: string[] = [];
  for (const tab of value) {
    if (
      !isRecord(tab) ||
      typeof tab.id !== 'string' ||
      typeof tab.label !== 'string' ||
      !isProjectHubChildComponentType(tab.componentType)
    ) {
      return undefined;
    }

    if (tab.enabled !== false) {
      tabIds.push(tab.id);
    }
  }

  return tabIds;
}

function getInheritedRefetchIds(component: Component): string[] {
  const configuredTabIds = getConfiguredProjectHubTabIds(component.properties.tabs);
  const tabIds =
    configuredTabIds !== undefined && configuredTabIds.length > 0
      ? configuredTabIds
      : DEFAULT_PROJECT_HUB_REFETCH_TAB_IDS;

  return [
    component.id,
    ...tabIds.map((tabId) => `${component.id}-${tabId}`),
  ];
}

export const SharedProjectHubComponent: React.FC<SharedProjectHubComponentProps> = ({
  component,
  isViewerMode = false,
}) => {
  const dataResolver = useComponentDataResolver(component);
  const refetchIds = useMemo(
    () =>
      dataResolver.source.binding === null
        ? [component.id]
        : getInheritedRefetchIds(component),
    [component, dataResolver.source.binding],
  );

  useEffect(() => {
    for (const refetchId of refetchIds) {
      registerRefetch(refetchId, dataResolver.refetch);
    }

    return () => {
      for (const refetchId of refetchIds) {
        unregisterRefetch(refetchId);
      }
    };
  }, [dataResolver.refetch, refetchIds]);

  // Loading state
  if (dataResolver.loading && dataResolver.rows.length === 0) {
    return (
      <div
        data-testid="project-hub-loading"
        className="w-full h-full flex items-center justify-center gap-2 text-muted-foreground text-sm"
      >
        <svg
          className="animate-spin"
          style={{ width: 20, height: 20 }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        Loading project hub...
      </div>
    );
  }

  // Error state
  if (dataResolver.error && dataResolver.rows.length === 0) {
    return (
      <div
        data-testid="project-hub-error"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span className="text-sm text-destructive">{dataResolver.error.message}</span>
        <button
          onClick={() => dataResolver.refetch()}
          style={{
            fontSize: 12,
            color: '#3b82f6',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <ProjectHubContainer
      component={component}
      data={dataResolver.rows}
      isViewerMode={isViewerMode}
      inheritedDataResolver={
        dataResolver.source.binding === null ? undefined : dataResolver
      }
    />
  );
};

SharedProjectHubComponent.displayName = 'SharedProjectHubComponent';
